// Browser WebAuthn plumbing shared by the passkey and step-up flows in
// `api/auth.ts`.
//
// The server (go-webauthn) speaks the W3C JSON wire format, where every binary
// field — challenge, credential ids, attestation blobs — is base64url-encoded;
// `navigator.credentials` speaks ArrayBuffers. Modern browsers bridge the two
// natively via PublicKeyCredential.parseCreationOptionsFromJSON /
// parseRequestOptionsFromJSON and credential.toJSON(); this module prefers those
// (they track the spec as it evolves) and falls back to a manual field-by-field
// codec for browsers that support WebAuthn but predate the JSON helpers
// (Safari < 17.4). No third-party dependency is worth this ~100 lines.
//
// Ported from bino-cloud (`cloud/ui/src/api/webauthn.ts`) unchanged in
// substance: `01` §4 makes passkeys the same ceremony here as there.

import { humanError } from "./errors.js";

/**
 * webauthnSupported reports whether this browser can run WebAuthn ceremonies at
 * all. It also requires a secure context: WebAuthn silently refuses on plain
 * http (except localhost), and checking here lets a page hide the passkey UI
 * instead of surfacing a confusing NotAllowedError later.
 */
export function webauthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    "PublicKeyCredential" in window &&
    typeof navigator.credentials?.create === "function"
  );
}

/**
 * conditionalMediationAvailable reports whether the browser can offer passkeys
 * in the email field's autofill dropdown (conditional UI). The sign-in page uses
 * it to decide whether to autostart a background `credentials.get()`; the
 * explicit "Sign in with a passkey" button works regardless.
 */
export async function conditionalMediationAvailable(): Promise<boolean> {
  if (!webauthnSupported()) return false;
  try {
    return (await PublicKeyCredential.isConditionalMediationAvailable?.()) ?? false;
  } catch {
    // Some browsers throw instead of resolving false; either way: no autofill.
    return false;
  }
}

/**
 * isWebAuthnCancel reports whether a ceremony rejection means the user dismissed
 * the browser prompt (NotAllowedError) or we aborted it ourselves (AbortError) —
 * non-events that deserve no feedback. Every other DOMException is a real
 * failure: SecurityError when the RP ID does not match the page's origin,
 * NotSupportedError, InvalidStateError. Treating all DOMExceptions as cancels
 * makes those silent no-ops and the passkey buttons look broken.
 */
export function isWebAuthnCancel(err: unknown): boolean {
  return (
    err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError")
  );
}

/**
 * passkeyFailureMessage renders a failed ceremony for a toast, or returns null
 * when the rejection is a cancel (no feedback due). Browser-side DOMExceptions
 * get passkey-specific copy; anything else is a server failure in the `apierr`
 * envelope and goes through the shared copy map.
 */
export function passkeyFailureMessage(err: unknown): string | null {
  if (isWebAuthnCancel(err)) return null;
  if (err instanceof DOMException) {
    if (err.name === "InvalidStateError") {
      return "This device already has a passkey for this account.";
    }
    return `Passkey request failed (${err.name}). This browser or address may not be set up for passkeys.`;
  }
  return humanError(err);
}

/** bufferToBase64url encodes bytes as unpadded base64url (the WebAuthn JSON alphabet). */
export function bufferToBase64url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** base64urlToBuffer decodes unpadded base64url into an ArrayBuffer. */
export function base64urlToBuffer(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

/**
 * The server's `begin` responses wrap the options the way go-webauthn marshals
 * them: `{"publicKey": {…}}` — the same envelope `navigator.credentials` takes,
 * which is also what the native `parse*FromJSON` helpers expect unwrapped.
 */
export interface CreationOptionsWire {
  publicKey: PublicKeyCredentialCreationOptionsJSON;
}

/** See [CreationOptionsWire]; this is the assertion half. */
export interface RequestOptionsWire {
  publicKey: PublicKeyCredentialRequestOptionsJSON;
}

/** parseCreationOptions turns a register/begin response into the ArrayBuffer
 * form `navigator.credentials.create` wants. */
export function parseCreationOptions(
  wire: CreationOptionsWire,
): PublicKeyCredentialCreationOptions {
  const json = wire.publicKey;
  if (typeof PublicKeyCredential.parseCreationOptionsFromJSON === "function") {
    return PublicKeyCredential.parseCreationOptionsFromJSON(json);
  }
  return {
    rp: json.rp,
    user: {
      name: json.user.name,
      displayName: json.user.displayName,
      id: base64urlToBuffer(json.user.id),
    },
    challenge: base64urlToBuffer(json.challenge),
    pubKeyCredParams: json.pubKeyCredParams,
    timeout: json.timeout,
    excludeCredentials: json.excludeCredentials?.map(descriptorFromJSON),
    authenticatorSelection: json.authenticatorSelection,
    attestation: json.attestation as AttestationConveyancePreference | undefined,
    extensions: json.extensions as AuthenticationExtensionsClientInputs | undefined,
  };
}

/** parseRequestOptions turns a login/begin response into the ArrayBuffer form
 * `navigator.credentials.get` wants. */
export function parseRequestOptions(wire: RequestOptionsWire): PublicKeyCredentialRequestOptions {
  const json = wire.publicKey;
  if (typeof PublicKeyCredential.parseRequestOptionsFromJSON === "function") {
    return PublicKeyCredential.parseRequestOptionsFromJSON(json);
  }
  return {
    challenge: base64urlToBuffer(json.challenge),
    timeout: json.timeout,
    rpId: json.rpId,
    allowCredentials: json.allowCredentials?.map(descriptorFromJSON),
    userVerification: json.userVerification as UserVerificationRequirement | undefined,
    extensions: json.extensions as AuthenticationExtensionsClientInputs | undefined,
  };
}

function descriptorFromJSON(d: PublicKeyCredentialDescriptorJSON): PublicKeyCredentialDescriptor {
  return {
    id: base64urlToBuffer(d.id),
    type: d.type as PublicKeyCredentialType,
    transports: d.transports as AuthenticatorTransport[] | undefined,
  };
}

/**
 * credentialToJSON serializes a finished ceremony's credential into the W3C JSON
 * wire format the server parses. The manual branch distinguishes attestation
 * (registration) from assertion (login) responses by their fields, since both
 * arrive as a PublicKeyCredential.
 */
export function credentialToJSON(cred: PublicKeyCredential): unknown {
  if (typeof cred.toJSON === "function") return cred.toJSON();
  const common = {
    id: cred.id,
    rawId: bufferToBase64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
  };
  if ("attestationObject" in cred.response) {
    const att = cred.response as AuthenticatorAttestationResponse;
    return {
      ...common,
      response: {
        clientDataJSON: bufferToBase64url(att.clientDataJSON),
        attestationObject: bufferToBase64url(att.attestationObject),
        // Transports let the server hint future allowCredentials; the method is
        // optional in older engines, and an empty list is a valid "don't know".
        transports: typeof att.getTransports === "function" ? att.getTransports() : [],
      },
    };
  }
  const asr = cred.response as AuthenticatorAssertionResponse;
  return {
    ...common,
    response: {
      clientDataJSON: bufferToBase64url(asr.clientDataJSON),
      authenticatorData: bufferToBase64url(asr.authenticatorData),
      signature: bufferToBase64url(asr.signature),
      userHandle: asr.userHandle ? bufferToBase64url(asr.userHandle) : undefined,
    },
  };
}

/**
 * devicePasskeyName derives a human default name for a passkey created on this
 * device ("Chrome on macOS"), so the passkey list stays recognizable even when
 * the user never renames anything. Best-effort UA sniffing is fine here — the
 * name is cosmetic and editable.
 */
export function devicePasskeyName(): string {
  const ua = navigator.userAgent;
  const browser = ua.includes("Edg/")
    ? "Edge"
    : ua.includes("OPR/") || ua.includes("Opera")
      ? "Opera"
      : ua.includes("Firefox/")
        ? "Firefox"
        : ua.includes("Chrome/")
          ? "Chrome"
          : ua.includes("Safari/")
            ? "Safari"
            : "";
  const os = /iPhone|iPad|iPod/.test(ua)
    ? "iOS"
    : ua.includes("Android")
      ? "Android"
      : ua.includes("Mac OS X")
        ? "macOS"
        : ua.includes("Windows")
          ? "Windows"
          : ua.includes("Linux")
            ? "Linux"
            : "";
  if (browser && os) return `${browser} on ${os}`;
  return browser || os || "This device";
}
